import { Search } from 'lucide-react';

import type { IssueCard } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { Table, type TableColumn } from '../../components/Table';
import { humanizeError } from '../../lib/errors';
import type { SortState } from './searchModel';
import styles from './SearchPage.module.css';

const PAGE_SIZE = 50;

export type SearchResultsProps = {
  readonly jql: string;
  readonly rows: readonly IssueCard[];
  readonly columns: readonly TableColumn<IssueCard>[];
  readonly total: number;
  readonly offset: number;
  readonly sort: SortState | null;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly onSortChange: (columnId: string) => void;
  readonly onOffsetChange: (offset: number) => void;
};

/** Таблица результатов JQL-запроса с сортировкой и постраничной навигацией. */
export function SearchResults({
  jql,
  rows,
  columns,
  total,
  offset,
  sort,
  isPending,
  isError,
  error,
  onRetry,
  onSortChange,
  onOffsetChange,
}: SearchResultsProps) {
  if (jql.trim().length === 0) {
    return (
      <div className={styles['results']}>
        <EmptyState
          icon={<Search size={20} />}
          title="Введите запрос"
          description="Например: project = DEMO AND priority = High ORDER BY created DESC"
        />
      </div>
    );
  }

  if (isPending) {
    return (
      <div className={styles['results']}>
        <div className={styles['skeleton']}>
          <Skeleton variant="row" count={8} />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles['results']}>
        <EmptyState
          icon={<Search size={20} />}
          title="Не удалось выполнить поиск"
          description={humanizeError(error).message}
          action={
            <Button variant="primary" onClick={onRetry}>
              Повторить
            </Button>
          }
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={styles['results']}>
        <EmptyState
          icon={<Search size={20} />}
          title="Ничего не найдено"
          description="Измените запрос или сбросьте условия."
        />
      </div>
    );
  }

  return (
    <div className={styles['results']}>
      <p className={styles['total']}>Найдено: {total}</p>
      <Table
        ariaLabel="Результаты поиска"
        columns={columns}
        rows={rows}
        getRowId={(issue) => issue.id}
        sort={sort}
        onSortChange={onSortChange}
      />
      <div className={styles['pagination']}>
        <Button size="sm" disabled={offset === 0} onClick={() => onOffsetChange(Math.max(0, offset - PAGE_SIZE))}>
          Назад
        </Button>
        <span className={styles['pageInfo']}>
          Стр. {Math.floor(offset / PAGE_SIZE) + 1} из {Math.max(1, Math.ceil(total / PAGE_SIZE))}
        </span>
        <Button size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => onOffsetChange(offset + PAGE_SIZE)}>
          Вперёд
        </Button>
      </div>
    </div>
  );
}
